import { Checkbox, NumberInput, Select, Switch, Textarea, TextInput } from '@mantine/core';
import { Controller } from 'react-hook-form';
import type { FieldDescriptor, FieldRegistry, LayoutDocument } from './types';
import type { ContractInputSchema } from '../schemas';

/**
 * Contracts edit-form field registry. Mirror of api/src/lib/contract-form-layout-schema.ts.
 *
 * Context bag is empty for v1 (all enum data is hard-coded; future could
 * add party-pickers, etc.).
 */

export const CONTRACT_FORM_FIELD_NAMES = [
  'contract_type',
  'status',
  'title',
  'contract_number',
  'effective_date',
  'execution_date',
  'expiration_date',
  'termination_date',
  'renewal_notice_date',
  'recorded',
  'recording_state',
  'recording_county',
  'recording_volume',
  'recording_page',
  'recording_instrument_number',
  'recording_date',
  'consideration_amount',
  'consideration_currency',
  'consideration_notes',
  'row_use',
  'row_pipeline_product',
  'row_width_feet',
  'row_length_feet',
  'row_length_rods',
  'row_pipeline_diameter_inches',
  'row_depth_restriction_feet',
  'row_surface_restoration_required',
  'township',
  'range',
  'section',
  'block',
  'lot',
  'abstract',
  'survey',
  'apn',
  'description',
  'notes',
] as const;

export type ContractFormFieldName = (typeof CONTRACT_FORM_FIELD_NAMES)[number];

// Context is currently empty — every contract field is self-contained.
export type ContractFormContext = Record<string, never>;

type ContractFieldDescriptor = FieldDescriptor<ContractInputSchema, ContractFormContext>;

const TYPE_OPTIONS = [{ value: 'row', label: 'ROW (right of way)' }];
const STATUS_OPTIONS: { value: 'draft' | 'active' | 'in_renewal' | 'expired' | 'terminated'; label: string }[] = [
  { value: 'draft', label: 'draft' },
  { value: 'active', label: 'active' },
  { value: 'in_renewal', label: 'in renewal' },
  { value: 'expired', label: 'expired' },
  { value: 'terminated', label: 'terminated' },
];

function textProps(descriptor: { label: string; placeholder?: string }, disabled: boolean) {
  const props: { label: string; placeholder?: string; disabled?: boolean } = {
    label: descriptor.label,
  };
  if (descriptor.placeholder !== undefined) props.placeholder = descriptor.placeholder;
  if (disabled) props.disabled = true;
  return props;
}

function textField(label: string, defaultSpan: number, opts: { placeholder?: string } = {}): ContractFieldDescriptor {
  const desc: ContractFieldDescriptor = {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <TextInput {...textProps(descriptor, disabled)} {...register(name)} />
    ),
  };
  if (opts.placeholder !== undefined) desc.placeholder = opts.placeholder;
  return desc;
}

function textareaField(label: string, defaultSpan: number): ContractFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <Textarea autosize minRows={2} {...textProps(descriptor, disabled)} {...register(name)} />
    ),
  };
}

function dateField(label: string, defaultSpan: number): ContractFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      // The contracts form uses a plain text-input date today (matches the
      // existing ContractFormDrawer behavior). Keep that to avoid changing
      // submit semantics in the same PR as the layout work.
      <TextInput
        type="date"
        {...textProps(descriptor, disabled)}
        {...register(name)}
      />
    ),
  };
}

function numberField(label: string, defaultSpan: number): ContractFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <NumberInput
            label={descriptor.label}
            decimalScale={2}
            value={(field.value as number | null) ?? ''}
            onChange={(v) => field.onChange(v === '' ? null : v)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

function checkboxField(label: string, defaultSpan: number): ContractFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Checkbox
            label={descriptor.label}
            checked={(field.value as boolean | null) ?? false}
            onChange={(e) => field.onChange(e.currentTarget.checked)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

export const CONTRACT_FORM_FIELDS: FieldRegistry<ContractInputSchema, ContractFormFieldName, ContractFormContext> = {
  contract_type: {
    label: 'Type',
    defaultSpan: 6,
    lockedOnEdit: true,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            label={descriptor.label}
            data={TYPE_OPTIONS}
            value={(field.value as string | null) ?? null}
            onChange={(v) => field.onChange(v ?? null)}
            disabled={disabled}
          />
        )}
      />
    ),
  },
  status: {
    label: 'Status',
    defaultSpan: 6,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            label={descriptor.label}
            data={STATUS_OPTIONS}
            value={(field.value as string | null) ?? null}
            onChange={(v) => field.onChange(v ?? null)}
            disabled={disabled}
          />
        )}
      />
    ),
  },
  title: textField('Title', 12),
  contract_number: textField('Contract number', 12),
  effective_date: dateField('Effective date', 6),
  execution_date: dateField('Execution date', 6),
  expiration_date: dateField('Expiration date', 6),
  termination_date: dateField('Termination date', 6),
  renewal_notice_date: dateField('Renewal notice date', 6),
  recorded: {
    label: 'Recorded?',
    defaultSpan: 12,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Switch
            label={descriptor.label}
            checked={(field.value as boolean | null) ?? false}
            onChange={(e) => field.onChange(e.currentTarget.checked)}
            disabled={disabled}
          />
        )}
      />
    ),
  },
  recording_state: textField('Recording state', 6),
  recording_county: textField('Recording county', 6),
  recording_volume: textField('Recording volume', 6),
  recording_page: textField('Recording page', 6),
  recording_instrument_number: textField('Recording instrument number', 8),
  recording_date: dateField('Recording date', 4),
  consideration_amount: numberField('Consideration amount', 4),
  consideration_currency: textField('Currency (ISO 4217)', 4, { placeholder: 'USD' }),
  consideration_notes: textField('Consideration notes', 4),
  row_use: { ...textField('ROW use', 6), visibleWhen: (v) => v.contract_type === 'row' },
  row_pipeline_product: { ...textField('Pipeline product', 6), visibleWhen: (v) => v.contract_type === 'row' },
  row_width_feet: { ...numberField('Width (ft)', 6), visibleWhen: (v) => v.contract_type === 'row' },
  row_length_feet: { ...numberField('Length (ft)', 6), visibleWhen: (v) => v.contract_type === 'row' },
  row_length_rods: { ...numberField('Length (rods)', 6), visibleWhen: (v) => v.contract_type === 'row' },
  row_pipeline_diameter_inches: {
    ...numberField('Pipeline diameter (in)', 6),
    visibleWhen: (v) => v.contract_type === 'row',
  },
  row_depth_restriction_feet: {
    ...numberField('Depth restriction (ft)', 6),
    visibleWhen: (v) => v.contract_type === 'row',
  },
  row_surface_restoration_required: {
    ...checkboxField('Surface restoration required', 6),
    visibleWhen: (v) => v.contract_type === 'row',
  },
  township: textField('Township', 4),
  range: textField('Range', 4),
  section: textField('Section', 4),
  block: textField('Block', 6),
  lot: textField('Lot', 6),
  abstract: textField('Abstract (TX)', 4),
  survey: textField('Survey (TX)', 4),
  apn: textField('APN', 4, { placeholder: "Assessor's parcel number" }),
  description: textareaField('Description', 12),
  notes: textareaField('Notes', 12),
};

/** Built-in default. Mirrors api/src/lib/contract-form-layout-schema.ts. */
export const CONTRACT_DEFAULT_LAYOUT: LayoutDocument<ContractFormFieldName> = {
  version: 1,
  groups: [
    {
      id: 'header',
      title: 'Header',
      fields: [
        { name: 'contract_type', span: 6 },
        { name: 'status', span: 6 },
        { name: 'title', span: 12 },
        { name: 'contract_number', span: 12 },
      ],
    },
    {
      id: 'lifecycle',
      title: 'Lifecycle',
      fields: [
        { name: 'effective_date', span: 6 },
        { name: 'execution_date', span: 6 },
        { name: 'expiration_date', span: 6 },
        { name: 'termination_date', span: 6 },
        { name: 'renewal_notice_date', span: 6 },
      ],
    },
    {
      id: 'recording',
      title: 'Recording / public records',
      fields: [
        { name: 'recorded', span: 12 },
        { name: 'recording_state', span: 6 },
        { name: 'recording_county', span: 6 },
        { name: 'recording_volume', span: 6 },
        { name: 'recording_page', span: 6 },
        { name: 'recording_instrument_number', span: 8 },
        { name: 'recording_date', span: 4 },
      ],
    },
    {
      id: 'compensation',
      title: 'Compensation',
      fields: [
        { name: 'consideration_amount', span: 4 },
        { name: 'consideration_currency', span: 4 },
        { name: 'consideration_notes', span: 4 },
      ],
    },
    {
      id: 'row_dimensions',
      title: 'ROW dimensions',
      fields: [
        { name: 'row_use', span: 6 },
        { name: 'row_pipeline_product', span: 6 },
        { name: 'row_width_feet', span: 6 },
        { name: 'row_length_feet', span: 6 },
        { name: 'row_length_rods', span: 6 },
        { name: 'row_pipeline_diameter_inches', span: 6 },
        { name: 'row_depth_restriction_feet', span: 6 },
        { name: 'row_surface_restoration_required', span: 6 },
      ],
    },
    {
      id: 'legal_anchor',
      title: 'Legal anchor',
      fields: [
        { name: 'township', span: 4 },
        { name: 'range', span: 4 },
        { name: 'section', span: 4 },
        { name: 'block', span: 6 },
        { name: 'lot', span: 6 },
        { name: 'abstract', span: 4 },
        { name: 'survey', span: 4 },
        { name: 'apn', span: 4 },
      ],
    },
    {
      id: 'free_text',
      title: 'Description & notes',
      fields: [
        { name: 'description', span: 12 },
        { name: 'notes', span: 12 },
      ],
    },
  ],
};
